import React, { useEffect, useState } from 'react'
import { ProjectProps } from '../Project'
import { AuthModel } from '../../../utils/interfaces'
import { useTranslation } from 'react-i18next'
import { Input } from '../../../components/Input'
import Loading from '../../../components/Loading'
import { Admin, updateItem } from '../../../api/API'
import { tables } from '../../../utils/constants'
import { BoldHeader3 } from '../../../components/General'


interface Props extends ProjectProps {
  auth: AuthModel
  setAuth: React.Dispatch<React.SetStateAction<AuthModel | null>>
}

const Password = ({ auth, setAuth, ...props }: Props) => {

  const { t } = useTranslation();
  const [minLength, setMinLength] = useState(auth.auth.passwordRequirements?.minLength || 8);
  const [requireNumber, setRequireNumber] = useState<boolean>(auth.auth.passwordRequirements?.requireNumber!);
  const [requireSymbol, setRequireSymbol] = useState<boolean>(auth.auth.passwordRequirements?.requireSymbol!);
  const [requireUppercase, setRequireUppercase] = useState<boolean>(auth.auth.passwordRequirements?.requireUppercase!);
  const [requireLowercase, setRequireLowercase] = useState<boolean>(auth.auth.passwordRequirements?.requireLowercase!);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    setMinLength(auth.auth.passwordRequirements?.minLength || 8);
    setRequireNumber(auth.auth.passwordRequirements?.requireNumber!);
    setRequireSymbol(auth.auth.passwordRequirements?.requireSymbol!);
    setRequireUppercase(auth.auth.passwordRequirements?.requireUppercase!);
    setRequireLowercase(auth.auth.passwordRequirements?.requireLowercase!);
  }, [auth.auth.passwordRequirements]);

  // check if requirements changed
  const isSameRequirements = () => {
    return auth.auth.passwordRequirements?.minLength === minLength &&
      auth.auth.passwordRequirements?.requireNumber === requireNumber &&
      auth.auth.passwordRequirements?.requireSymbol === requireSymbol &&
      auth.auth.passwordRequirements?.requireUppercase === requireUppercase &&
      auth.auth.passwordRequirements?.requireLowercase === requireLowercase;
  }

  const saveUpdate = async () => {
    setIsSaving(true);
    await Admin.updatePasswordRequirements(auth.auth.userPoolId, minLength, requireNumber, requireSymbol, requireUppercase, requireLowercase);
    const newAuth: AuthModel = { ...auth, auth: { ...auth.auth, passwordRequirements: { minLength, requireNumber, requireSymbol, requireUppercase, requireLowercase } } };
    setAuth(newAuth);
    await updateItem(tables.auths, newAuth.id, { auth: newAuth.auth });
    setIsSaving(false);
  }

  return (
    <div className='flex-1 p-2 flex flex-col'>
      <div className='flex items-center justify-between flex-wrap mt-2'>
        <BoldHeader3>{t('project.auth.password')}</BoldHeader3>
      </div>
      <div className='flex flex-col flex-1 overflow-auto mt-2'>
        <div className='flex flex-col'>
          <div className='flex flex-col mt-2'>
            <label className='text-xs text-gray-400 m-1'>{t('project.auth.password.min-length')}</label>
            <Input value={minLength} style={{ maxWidth: '240px' }} type='number' min={6} max={99} onChange={(e) => setMinLength(parseInt(e.target.value))} />
          </div>
          <div className='flex items-center cursor-pointer mt-2' onClick={() => setRequireNumber(!requireNumber)}>
            <button className={`h-4 w-4 rounded mr-2 border flex items-center justify-center ${requireNumber ? 'bg-primary border-primary' : 'bg-none'}`}><i className='fa-solid fa-check text-xs text-white' /></button>
            <span className='text-sm flex-1'>{t('project.auth.password.require-number')}</span>
          </div>
          <div className='flex items-center cursor-pointer mt-2' onClick={() => setRequireSymbol(!requireSymbol)}>
            <button className={`h-4 w-4 rounded mr-2 border flex items-center justify-center ${requireSymbol ? 'bg-primary border-primary' : 'bg-none'}`}><i className='fa-solid fa-check text-xs text-white' /></button>
            <span className='text-sm flex-1'>{t('project.auth.password.require-symbol')} ({`^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < ' : ; | _ ~ \` + =`})</span>
          </div>
          <div className='flex items-center cursor-pointer mt-2' onClick={() => setRequireLowercase(!requireLowercase)}>
            <button className={`h-4 w-4 rounded mr-2 border flex items-center justify-center ${requireLowercase ? 'bg-primary border-primary' : 'bg-none'}`}><i className='fa-solid fa-check text-xs text-white' /></button>
            <span className='text-sm flex-1'>{t('project.auth.password.require-lowercase')}</span>
          </div>
          <div className='flex items-center cursor-pointer mt-2' onClick={() => setRequireUppercase(!requireUppercase)}>
            <button className={`h-4 w-4 rounded mr-2 border flex items-center justify-center ${requireUppercase ? 'bg-primary border-primary' : 'bg-none'}`}><i className='fa-solid fa-check text-xs text-white' /></button>
            <span className='text-sm flex-1'>{t('project.auth.password.require-uppercase')}</span>
          </div>
          <div className='mt-8'>
            <button onClick={saveUpdate} disabled={minLength < 6 || minLength > 99 || isSameRequirements() || isSaving} className='p-2 px-8 disabled:bg-gray-300 bg-primary rounded-lg text-sm text-white'>{isSaving ? <Loading /> : t('general.save')}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Password